import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { format } from "date-fns";
import { PromiseStatuses } from "../../utils";
import { AssignmentCreationDTO, AssignmentDTO, AssignmentErrorsDTO, assignmentLetterTypeEnum, AssignmentUpdateDTO, AssignmentUploadDTO, FileSignedErrorsDTO, GetAllAssignmentDTO, GetAllAssignmentFiltersDTO, GetAllInspectionVisitDTO, GetAllInspectionVisitFiltersDTO, GetAllResultControlActivityDTO, GetAllResultControlActivityFiltersDTO, InspectionVisitCreationDTO, InspectionVisitDTO, InspectionVisitUpdateDTO, ResultControlActivityCreationDTO, ResultControlActivityDTO, ResultControlActivityUpdateDTO, TurnOverResponseDTO } from "./dataservice/dto";
import { NewAssigmentService } from "./dataservice/service";

interface AssignmentState {
    /* ASSIGNMENT */
    onEdit: boolean

    // REQUEST
    assignmentCreationRequest: AssignmentCreationDTO
    assignmentUpdateRequest: AssignmentUpdateDTO
    uploadFileAssignment?: AssignmentUploadDTO
    fileAssignSigned?: File
    assignmentIdToUpdate: string
    assignmentSelected: string
    assignmentPMId: string
    assignmentProtocolTextPopup: string
    turnOverResponse?: TurnOverResponseDTO
    inspectionVisitToDelete: {
        idIvToDelete: string
        companyName: string
    }
    assignedSignedFileId?: number

    // ERRORS
    assignmentErrors: AssignmentErrorsDTO
    fileAssSignedErrors: FileSignedErrorsDTO

    // FILTERS
    assignmentFilters: GetAllAssignmentFiltersDTO

    // STATUS
    assignmentValidationStatus: PromiseStatuses
    assignmentCreationStatus: PromiseStatuses
    assignmentUpdateStatus: PromiseStatuses
    assignmentDeleteStatus: PromiseStatuses
    getAllAssignmentStatus: PromiseStatuses
    getAssignmentByIdStatus: PromiseStatuses
    fileAssignSignedValidationStatus: PromiseStatuses
    fileAssignSignedUploadStatus: PromiseStatuses

    // RESPONSE
    assignmentCreationResponse?: string
    getAllAssignmentResponse?: GetAllAssignmentDTO
    getAssignmentByIdResponse?: AssignmentDTO

    /* INSPECTION VISIT */
    // REQUEST
    inspectionVisitCreationRequest: InspectionVisitCreationDTO
    inspectionVisitUpdateRequest: InspectionVisitUpdateDTO
    inspectionVisitIdToUpdate: string
    ivFileToUpload: File | null
    ivUploadedFileId?: string

    // FILTERS
    inspectionVisitFilters: GetAllInspectionVisitFiltersDTO

    // STATUS
    inspectionVisitCreationStatus: PromiseStatuses
    inspectionVisitUpdateStatus: PromiseStatuses
    inspectionVisitDeleteStatus: PromiseStatuses
    getAllInspectionVisitStatus: PromiseStatuses
    getInspectionVisitByIdStatus: PromiseStatuses
    inspectionVisitUploadFileStatus: PromiseStatuses
    inspectionVisitTurnOverStatus: PromiseStatuses

    // RESPONSE
    getAllInspectionVisitResponse?: GetAllInspectionVisitDTO
    getInspectionVisitByIdResponse?: InspectionVisitDTO
    inspectionVisitUploadFileResponse?: string

    /* RESULT CONTROL ACTIVITY */
    rcaCreationRequest: ResultControlActivityCreationDTO
    rcaUpdateRequest: ResultControlActivityUpdateDTO
    rcaIdToUpdate: string
    rcaIVId: string
    rcaIVCustomerId: string

    // FILTERS
    rcaFilters: GetAllResultControlActivityFiltersDTO

    // STATUS
    rcaCreationStatus: PromiseStatuses
    rcaUpdateStatus: PromiseStatuses
    rcaDeleteStatus: PromiseStatuses
    getAllRcaStatus: PromiseStatuses
    getRcaByIdStatus: PromiseStatuses

    // RESPONSE
    getAllRcaResponse?: GetAllResultControlActivityDTO
    getRcaByIdResponse?: ResultControlActivityDTO
}

const initialState: AssignmentState = {
    onEdit: false,
    assignmentCreationRequest: {},
    assignmentUpdateRequest: {},
    assignmentIdToUpdate: '',
    assignmentProtocolTextPopup: '',
    inspectionVisitToDelete: {
        idIvToDelete: '',
        companyName: ''
    },
    assignmentPMId: '',
    assignmentSelected: '',

    assignmentErrors: {
        personalManagementId: false,
        departureProtocolId: false,
        norm: false,
        assignmentPlace: false,
        assignmentDate: false,
        year: false,
        headQuarterId: false,
    },
    fileAssSignedErrors: {
        file: false
    },

    assignmentFilters: {
        itemsPerPage: 0,
        page: 0,
    },

    assignmentValidationStatus: 'idle',
    assignmentCreationStatus: 'idle',
    assignmentUpdateStatus: 'idle',
    assignmentDeleteStatus: 'idle',
    getAllAssignmentStatus: 'idle',
    getAssignmentByIdStatus: 'idle',
    fileAssignSignedValidationStatus: 'idle',
    fileAssignSignedUploadStatus: 'idle',
    inspectionVisitTurnOverStatus: 'idle',

    inspectionVisitCreationRequest: {
        activityTypes: [],
        auditedActivities: [],
        addOnIds: [],
        sqnpiControlActivities: []
    },
    inspectionVisitUpdateRequest: {
        startingEndingVisits: [],
        activityTypes: [],
        auditedActivities: [],
        addOnIds: [],
        sqnpiControlActivities: [],
        daysNumber: 0
    },
    inspectionVisitIdToUpdate: '',
    ivFileToUpload: null,

    inspectionVisitFilters: {
        itemsPerPage: 0,
        page: 0
    },

    inspectionVisitCreationStatus: 'idle',
    inspectionVisitUpdateStatus: 'idle',
    inspectionVisitDeleteStatus: 'idle',
    getAllInspectionVisitStatus: 'idle',
    getInspectionVisitByIdStatus: 'idle',
    inspectionVisitUploadFileStatus: 'idle',

    rcaCreationRequest: {},
    rcaUpdateRequest: {},
    rcaIdToUpdate: '',
    rcaIVId: '',
    rcaIVCustomerId: '',

    rcaFilters: {
        itemsPerPage: 0,
        page: 0
    },

    rcaCreationStatus: 'idle',
    rcaUpdateStatus: 'idle',
    rcaDeleteStatus: 'idle',
    getAllRcaStatus: 'idle',
    getRcaByIdStatus: 'idle'
}

export const AssignmentValidation = createAsyncThunk(
    'Assignment/Validation',
    async (request: AssignmentCreationDTO, thunkApi): Promise<void> => {
        let isValid = true
        thunkApi.dispatch(resetAssignmentErrors())

        if (request.personalManagementId === undefined) {
            thunkApi.dispatch(setValidatePersonalManagementId(true))
            isValid = false
        }

        if (request.departureProtocolId === undefined) {
            thunkApi.dispatch(setValidateDepartureProtocolId(true))
            isValid = false
        }

        if (request.norm === undefined) {
            thunkApi.dispatch(setValidateNorm(true))
            isValid = false
        }

        if (request.assignmentPlace === undefined) {
            thunkApi.dispatch(setValidateAssignmentPlace(true))
            isValid = false
        }

        if (request.assignmentDate === undefined) {
            thunkApi.dispatch(setValidateAssignmentDate(true))
            isValid = false
        }

        if (request.year === undefined) {
            thunkApi.dispatch(setValidateYear(true))
            isValid = false
        }

        if (request.headQuarterId === undefined) {
            thunkApi.dispatch(setValidateHeadQuarterId(true))
            isValid = false
        }

        if (!isValid) {
            return Promise.reject()
        }
        return Promise.resolve()

    }
)

export const AssignmentCreation = createAsyncThunk(
    'Assignment/Creation',
    async (data: AssignmentCreationDTO, thunkApi): Promise<string> => {
        const AssignmentService = NewAssigmentService()
        return AssignmentService.CreateAssignment(data).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const UploadFileAssSigned = createAsyncThunk(
    'Assignment/UploadFileAssSigned',
    async (request: { body: FormData, id: string }, thunkApi): Promise<void> => {
        const AssignmentService = NewAssigmentService();

        return AssignmentService.UploadFileAssSigned(request.body, request.id).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const fileAssignSignedValidation = createAsyncThunk(
    'Assignment/fileAssignSignedValidation',
    async (request: { file: File }, thunkApi): Promise<void> => {
        let isValid = true
        thunkApi.dispatch(resetFileAssSigned())

        if (request.file === undefined) {
            thunkApi.dispatch(setValidateFileAssSisgned(true))
            isValid = false
        }
        if (!isValid) {
            return Promise.reject()
        }
        return Promise.resolve()

    }
)

export const AssignmentUpdate = createAsyncThunk(
    'Assignment/Update',
    async (request: { body: AssignmentUpdateDTO, id: string }, thunkApi): Promise<void> => {
        const AssignmentService = NewAssigmentService()
        return AssignmentService.UpdateAssignment(request.body, request.id).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const AssignmentDelete = createAsyncThunk(
    'Assignment/Delete',
    async (id: string, thunkApi): Promise<void> => {
        const AssignmentService = NewAssigmentService()
        return AssignmentService.DeleteAssignment(id).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const GetAllAssignment = createAsyncThunk(
    'Assignment/GetAll',
    async (filters: GetAllAssignmentFiltersDTO, thunkApi): Promise<GetAllAssignmentDTO> => {
        const AssignmentService = NewAssigmentService()
        return AssignmentService.GetAllAssignment(filters).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const GetAssignmentById = createAsyncThunk(
    'Assignment/GetById',
    async (id: string, thunkApi): Promise<AssignmentDTO> => {
        const AssignmentService = NewAssigmentService()
        return AssignmentService.GetAssignmentById(id).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const DownloadAssignmentLetter = createAsyncThunk(
    'register/DownloadAssignmentLetter',
    async (data: { id: string, infoType: assignmentLetterTypeEnum }, thunkApi): Promise<void> => {
        const AssignmentService = NewAssigmentService()

        return AssignmentService.DownloadAssignmentLetter(data).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)


export const InspectionVisitCreation = createAsyncThunk(
    'InspectionVisit/Creation',
    async (data: InspectionVisitCreationDTO, thunkApi): Promise<void> => {
        const AssignmentService = NewAssigmentService()
        return AssignmentService.CreateInspectionVisit(data).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const InspectionVisitUpdate = createAsyncThunk(
    'InspectionVisit/Update',
    async (request: { body: InspectionVisitUpdateDTO, id: string }, thunkApi): Promise<void> => {
        const AssignmentService = NewAssigmentService()
        return AssignmentService.UpdateInspectionVisit(request.body, request.id).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const InspectionVisitDelete = createAsyncThunk(
    'InspectionVisit/Delete',
    async (id: string, thunkApi): Promise<void> => {
        const AssignmentService = NewAssigmentService()
        return AssignmentService.DeleteInspectionVisit(id).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const GetAllInspectionVisit = createAsyncThunk(
    'InspectionVisit/GetAll',
    async (filters: GetAllInspectionVisitFiltersDTO, thunkApi): Promise<GetAllInspectionVisitDTO> => {
        const AssignmentService = NewAssigmentService()
        return AssignmentService.GetAllInspectionVisit(filters).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const InspectionVisitTurnOver = createAsyncThunk(
    'InspectionVisit/InspectionVisitTurnOver',
    async (arg: { inspectorCode: string; customerId: string }, thunkApi): Promise<TurnOverResponseDTO> => {
        const AssignmentService = NewAssigmentService()
        const { inspectorCode, customerId } = arg;
        return AssignmentService.InspectionVisitTurnOver(inspectorCode, customerId).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const GetInspectionVisitById = createAsyncThunk(
    'InspectionVisit/GetById',
    async (id: string, thunkApi): Promise<InspectionVisitDTO> => {
        const AssignmentService = NewAssigmentService()
        return AssignmentService.GetInspectionVisitById(id).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const InspectionVisitUploadFile = createAsyncThunk(
    'InspectionVisit/UploadFile',
    async (request: { data: FormData, id: string }, thunkApi): Promise<string> => {
        const AssignmentService = NewAssigmentService()
        return AssignmentService.InspectionVisitUploadFile(request.data, request.id).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const ResultControlActivityCreation = createAsyncThunk(
    'ResultControlActivity/Creation',
    async (data: ResultControlActivityCreationDTO, thunkApi): Promise<void> => {
        const AssignmentService = NewAssigmentService()
        return AssignmentService.CreateResultControlActivity(data).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const ResultControlActivityUpdate = createAsyncThunk(
    'ResultControlActivity/Update',
    async (request: { body: ResultControlActivityUpdateDTO, id: string }, thunkApi): Promise<void> => {
        const AssignmentService = NewAssigmentService()
        return AssignmentService.UpdateResultControlActivity(request.body, request.id).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const ResultControlActivityDelete = createAsyncThunk(
    'ResultControlActivity/Delete',
    async (id: string, thunkApi): Promise<void> => {
        const AssignmentService = NewAssigmentService()
        return AssignmentService.DeleteResultControlActivity(id).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const GetAllResultControlActivity = createAsyncThunk(
    'ResultControlActivity/GetAll',
    async (filters: GetAllResultControlActivityFiltersDTO, thunkApi): Promise<GetAllResultControlActivityDTO> => {
        const AssignmentService = NewAssigmentService()
        return AssignmentService.GetAllResultControlActivity(filters).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const GetResultControlActivityById = createAsyncThunk(
    'ResultControlActivity/getById',
    async (id: string, thunkApi): Promise<ResultControlActivityDTO> => {
        const AssignmentService = NewAssigmentService()
        return AssignmentService.GetResultControlActivityById(id).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

const AssignmentSlice = createSlice({
    name: 'assignment/slice',
    initialState,
    reducers: {
        /* ASSIGNMENT */
        setOnEdit: (state, action) => {
            state.onEdit = action.payload
        },

        setIVToDelete: (state, action) => {
            state.inspectionVisitToDelete = action.payload
        },
        setAssignmentProtocolTextPopup: (state, action) => {
            state.assignmentProtocolTextPopup = action.payload
        },

        setAssignmentSignedFileId: (state, action) => {
            state.assignedSignedFileId = action.payload
        },
        setAssignmentSelected: (state, action) => {
            state.assignmentSelected = action.payload
        },

        // REQUEST [CREATION]
        setAssCreationAssignmentDate: (state, action) => {
            state.assignmentCreationRequest.assignmentDate = action.payload
        },
        setAssCreationYear: (state, action) => {
            state.assignmentCreationRequest.year = action.payload
        },
        setAssCreationAssignmentPlace: (state, action) => {
            state.assignmentCreationRequest.assignmentPlace = action.payload
        },
        setAssCreationNorm: (state, action) => {
            state.assignmentCreationRequest.norm = action.payload
        },
        setAssCreationDepartureProtocolId: (state, action) => {
            state.assignmentCreationRequest.departureProtocolId = action.payload
        },
        setAssCreationPersonalManagementId: (state, action) => {
            state.assignmentCreationRequest.personalManagementId = action.payload
        },
        setAssCreationPmContractId: (state, action) => {
            state.assignmentCreationRequest.pmContractId = action.payload
        },
        setAssCreationReference: (state, action) => {
            state.assignmentCreationRequest.reference = action.payload
        },
        setAssContractType: (state, action) => {
            state.assignmentCreationRequest.contractType = action.payload
        },
        setAssHeadQuarterId: (state, action) => {
            state.assignmentCreationRequest.headQuarterId = action.payload
        },
        resetAssignmentCreationRequest: (state) => {
            state.assignmentCreationRequest = {}
        },

        // REQUEST [UPDATE]
        setAssignmentIdToUpdate: (state, action) => {
            state.assignmentIdToUpdate = action.payload
        },
        setAssignmentUpdateRequest: (state, action) => {
            state.assignmentUpdateRequest = action.payload
        },
        setAssignmentPMId: (state, action) => {
            state.assignmentPMId = action.payload
        },
        setAssignmentUpdateIsAccepted: (state, action) => {
            state.assignmentUpdateRequest.isAccepted = action.payload
        },
        setAssUpdatePMContractId: (state, action) => {
            state.assignmentUpdateRequest.pmContractId = action.payload
        },
        setAssUpdateReference: (state, action) => {
            state.assignmentUpdateRequest.reference = action.payload
        },
        setAssUpdateRevocationProtocolId: (state, action) => {
            state.assignmentUpdateRequest.revocationProtocolId = action.payload
        },
        setAssUpdateState: (state, action) => {
            state.assignmentUpdateRequest.state = action.payload
        },
        setAssUpdateSignedAssignmentLetterId: (state, action) => {
            state.assignmentUpdateRequest.signedAssignmentLetterId = action.payload
        },
        setAssUpdateDepartureProtocolId: (state, action) => {
            state.assignmentUpdateRequest.departureProtocolId = action.payload
        },
        setAssUpdateAssignmentPlace: (state, action) => {
            state.assignmentUpdateRequest.assignmentPlace = action.payload
        },
        setAssUpdateAssignmentUcoVerDate: (state, action) => {
            state.assignmentUpdateRequest.ucoVerificationDate = action.payload
        },
        setAssUpdateAssignmentUcoVerUserId: (state, action) => {
            state.assignmentUpdateRequest.ucoVerificationOfficerId = action.payload
        },
        setFileAssignSigned: (state, action) => {
            state.fileAssignSigned = action.payload
        },
        resetAssignmentUpdateRequest: (state) => {
            state.assignmentUpdateRequest = {}
        },
        resetFileAssSignedValidationStatus: (state) => {
            state.fileAssignSignedValidationStatus = 'idle'
        },

        // ERRORS
        setValidatePersonalManagementId: (state, action) => {
            state.assignmentErrors.personalManagementId = action.payload
        },
        setValidateDepartureProtocolId: (state, action) => {
            state.assignmentErrors.departureProtocolId = action.payload
        },
        setValidateNorm: (state, action) => {
            state.assignmentErrors.norm = action.payload
        },
        setValidateAssignmentPlace: (state, action) => {
            state.assignmentErrors.assignmentPlace = action.payload
        },
        setValidateAssignmentDate: (state, action) => {
            state.assignmentErrors.assignmentDate = action.payload
        },
        setValidateYear: (state, action) => {
            state.assignmentErrors.year = action.payload
        },
        setValidateHeadQuarterId: (state, action) => {
            state.assignmentErrors.headQuarterId = action.payload
        },
        resetAssignmentErrors: (state) => {
            state.assignmentErrors = {
                personalManagementId: false,
                departureProtocolId: false,
                norm: false,
                assignmentPlace: false,
                assignmentDate: false,
                year: false,
                headQuarterId: false,
            }
        },
        setValidateFileAssSisgned: (state, action) => {
            state.fileAssSignedErrors.file = action.payload
        },

        // FILTERS
        setAssignmentFilterItemsPerPage: (state, action) => {
            state.assignmentFilters.itemsPerPage = action.payload
        },
        setAssignmentFilterPage: (state, action) => {
            state.assignmentFilters.page = action.payload
        },
        setAssignmentFilterOrder: (state, action) => {
            state.assignmentFilters.order = action.payload
        },
        setAssignmentFilterSort: (state, action) => {
            state.assignmentFilters.sort = action.payload
        },
        setAssignmentFilterYear: (state, action) => {
            state.assignmentFilters.year = action.payload
        },
        setAssignmentFilterInspectorCode: (state, action) => {
            state.assignmentFilters.inspectorCode = action.payload
        },
        resetAssignmentFilters: (state) => {
            state.assignmentFilters = {
                itemsPerPage: 0,
                page: 0
            }
        },

        // STATUS
        resetAssignmentValidationStatus: (state) => {
            state.assignmentValidationStatus = 'idle'
        },
        resetAssigmentCreationStatus: (state) => {
            state.assignmentCreationStatus = 'idle'
        },
        resetAssigmentUpdateStatus: (state) => {
            state.assignmentUpdateStatus = 'idle'
        },
        resetAssigmentDeleteStatus: (state) => {
            state.assignmentDeleteStatus = 'idle'
        },
        resetGetAllAssignmentStatus: (state) => {
            state.getAllAssignmentStatus = 'idle'
        },
        resetGetAssignmentByIdStatus: (state) => {
            state.getAssignmentByIdStatus = 'idle'
        },
        resetTurnOverStatus: (state) => {
            state.inspectionVisitTurnOverStatus = 'idle'
        },

        // RESPONSE
        resetAssignmentCreationResponse: (state) => {
            state.assignmentCreationResponse = undefined
        },
        resetGetAssignmentByIdResponse: (state) => {
            state.getAssignmentByIdResponse = undefined
        },

        /* INSPECTION VISIT */
        // REQUEST [CREATION]
        setIVCreationAssignmentId: (state, action) => {
            state.inspectionVisitCreationRequest.assignmentId = action.payload
        },
        setIVCreationCustomerId: (state, action) => {
            state.inspectionVisitCreationRequest.customerId = action.payload
        },
        setIVCreationAnnualControlPlaneDetailId: (state, action) => {
            state.inspectionVisitCreationRequest.annualControlPlaneDetailId = action.payload
        },
        setIVCreationType: (state, action) => {
            state.inspectionVisitCreationRequest.type = action.payload
        },
        setIVCreationMode: (state, action) => {
            state.inspectionVisitCreationRequest.mode = action.payload
        },
        setIVCreationExpirationDate: (state, action) => {
            state.inspectionVisitCreationRequest.expirationDate = action.payload
        },
        setIVCreationLicenseeOperator: (state, action) => {
            state.inspectionVisitCreationRequest.licenseeOperator = action.payload
        },
        setIVCreationControlDetail: (state, action) => {
            state.inspectionVisitCreationRequest.controlDetail = action.payload
        },
        setIVCreationSpecification: (state, action) => {
            state.inspectionVisitCreationRequest.specification = action.payload
        },
        setIVCreationBioNotificationId: (state, action) => {
            state.inspectionVisitCreationRequest.bioNotificationId = action.payload
        },
        setIVCreationInSupportSubject: (state, action) => {
            state.inspectionVisitCreationRequest.inSupportSubject = action.payload
        },
        setIVCreationInSupportSubjectEntity: (state, action) => {
            state.inspectionVisitCreationRequest.inSupportSubjectEntity = action.payload
        },
        setIVCreationInAccompanyingSubject: (state, action) => {
            state.inspectionVisitCreationRequest.inAccompanyingSubject = action.payload
        },
        setIVCreationInAccompanyingSubjectEntity: (state, action) => {
            state.inspectionVisitCreationRequest.inAccompanyingSubjectEntity = action.payload
        },
        setIVCreationExpertSubject: (state, action) => {
            state.inspectionVisitCreationRequest.expertSubject = action.payload
        },
        setIVCreationTraineeTechnician: (state, action) => {
            state.inspectionVisitCreationRequest.traineeTechnician = action.payload
        },
        setIVCreationPlannedSampling: (state, action) => {
            state.inspectionVisitCreationRequest.plannedSampling = action.payload
        },
        setIVCreationAuditedActivities: (state, action) => {
            state.inspectionVisitCreationRequest.auditedActivities = action.payload
        },
        setIVCreationActivity: (state, action) => {
            console.log(action.payload)
            state.inspectionVisitCreationRequest.activity = action.payload
        },
        setIVCreationActivityTypes: (state, action) => {
            console.log('activ ty ', action.payload)
            state.inspectionVisitCreationRequest.activityTypes = action.payload
        },
        setIVCreationDocumentalControl: (state, action) => {
            state.inspectionVisitCreationRequest.documentalControl = action.payload
        },
        setIVCreationRemoteVisit: (state, action) => {
            state.inspectionVisitCreationRequest.remoteVisit = action.payload
        },
        setIVCreationInLocoControl: (state, action) => {
            state.inspectionVisitCreationRequest.inLocoControl = action.payload
        },
        setIVCreationExpirationDateExtended: (state, action) => {
            state.inspectionVisitCreationRequest.expirationDateExtended = action.payload
        },
        setIVCreationOfficerId: (state, action) => {
            state.inspectionVisitCreationRequest.officerId = action.payload
        },
        setIVCreationSqnpiControlActivities: (state, action) => {
            state.inspectionVisitCreationRequest.sqnpiControlActivities = action.payload
        },
        setIVCreationGGExpert: (state, action) => {
            state.inspectionVisitCreationRequest.ggExpert = action.payload
        },
        setIVCreationGGObserver: (state, action) => {
            state.inspectionVisitCreationRequest.ggObserver = action.payload
        },
        setIVCreationGGInspector: (state, action) => {
            state.inspectionVisitCreationRequest.ggInspector = action.payload
        },
        setIVCreationGGTeamLeader: (state, action) => {
            state.inspectionVisitCreationRequest.ggTeamLeader = action.payload
        },
        setIVCreationGGProducts: (state, action) => {
            state.inspectionVisitCreationRequest.ggProducts = action.payload
        },
        setIVCreationGGControlDuration: (state, action) => {
            state.inspectionVisitCreationRequest.ggControlDuration = action.payload
        },
        setIVCreationGGLotNumber: (state, action) => {
            state.inspectionVisitCreationRequest.ggLotNumber = action.payload
        },
        setIVCreationAddOnIds: (state, action) => {
            state.inspectionVisitCreationRequest.addOnIds = action.payload
        },
        setIVCreationFourthEnvelope: (state, action) => {
            state.inspectionVisitCreationRequest.fourthEnvelope = action.payload
        },
        setIVCreationThirdEnvelope: (state, action) => {
            state.inspectionVisitCreationRequest.thirdEnvelope = action.payload
        },
        setIVCreationSecondEnvelope: (state, action) => {
            state.inspectionVisitCreationRequest.secondEnvelope = action.payload
        },
        setIVCreationFirstEnvelope: (state, action) => {
            state.inspectionVisitCreationRequest.firstEnvelope = action.payload
        },
        setIVCreationMaterialType: (state, action) => {
            state.inspectionVisitCreationRequest.materialType = action.payload
        },
        resetInspectionVisitRequest: (state) => {
            state.inspectionVisitCreationRequest = {
                activityTypes: [],
                auditedActivities: [],
                addOnIds: [],
                sqnpiControlActivities: []
            }
        },

        // REQUEST [UPDATE]
        setIVFileToUpload: (state, action) => {
            state.ivFileToUpload = action.payload
        },
        setIVUploadedFileId: (state, action) => {
            state.ivUploadedFileId = action.payload
        },
        setInspectionVisitUpdateRequest: (state, action) => {
            state.inspectionVisitUpdateRequest = action.payload
        },
        setInspectionVisitIdToUpdate: (state, action) => {
            state.inspectionVisitIdToUpdate = action.payload
        },
        setIVUpdateType: (state, action) => {
            state.inspectionVisitUpdateRequest.type = action.payload
        },
        setIVUpdateMode: (state, action) => {
            state.inspectionVisitUpdateRequest.mode = action.payload
        },
        setIVUpdateExpirationDate: (state, action) => {
            state.inspectionVisitUpdateRequest.expirationDate = action.payload
        },
        setIVUpdateLicenseeOperator: (state, action) => {
            state.inspectionVisitUpdateRequest.licenseeOperator = action.payload
        },
        setIVUpdateControlDetail: (state, action) => {
            state.inspectionVisitUpdateRequest.controlDetail = action.payload
        },
        setIVUpdateSpecification: (state, action) => {
            state.inspectionVisitUpdateRequest.specification = action.payload
        },
        setIVUpdateBioNotificationId: (state, action) => {
            state.inspectionVisitUpdateRequest.bioNotificationId = action.payload
        },
        setIVUpdatePlannedVisitDate: (state, action) => {
            state.inspectionVisitUpdateRequest.plannedVisitDate = action.payload
        },
        setIVUpdateEffectiveVisitDate: (state, action) => {
            state.inspectionVisitUpdateRequest.effectiveVisitDate = action.payload
        },
        setIVUpdateReportProtocolId: (state, action) => {
            state.inspectionVisitUpdateRequest.reportProtocolId = action.payload
        },
        setIVUpdateDocumentalControl: (state, action) => {
            state.inspectionVisitUpdateRequest.documentalControl = action.payload
        },
        setIVUpdateRemoteVisit: (state, action) => {
            state.inspectionVisitUpdateRequest.remoteVisit = action.payload
        },
        setIVUpdateRemoteVisitProtocolId: (state, action) => {
            state.inspectionVisitUpdateRequest.remoteVisitProtocolId = action.payload
        },
        setIVUpdateInMoreDays: (state, action) => {
            state.inspectionVisitUpdateRequest.inMoreDays = action.payload
        },
        setIVUpdateDaysNumber: (state, action) => {
            state.inspectionVisitUpdateRequest.daysNumber = action.payload
        },
        setIVUpdateStartingEndingVisits: (state, action) => {
            state.inspectionVisitUpdateRequest.startingEndingVisits = action.payload
        },
        addIVUpdateDefaultStartingEndingVisits: (state) => {
            state.inspectionVisitUpdateRequest.startingEndingVisits.push(`${format(new Date(), 'yyyy-MM-dd')}@00:00@00:00`)
        },
        addIVUpdateStartingEndingVisits: (state, action) => {
            state.inspectionVisitUpdateRequest.startingEndingVisits.push(action.payload)
        },
        setIVUpdateStartingEndingVisitsByIndex: (state, action) => {
            state.inspectionVisitUpdateRequest.startingEndingVisits[action.payload.index] = action.payload.data
        },
        setIVUpdateVisitDate: (state, action) => {
            state.inspectionVisitUpdateRequest.visitDate = action.payload
        },
        setIVUpdateResult: (state, action) => {
            state.inspectionVisitUpdateRequest.result = action.payload
        },
        setIVUpdateNonComplianceType: (state, action) => {
            state.inspectionVisitUpdateRequest.nonComplianceType = action.payload
        },
        setIVUpdateInstructorNote: (state, action) => {
            state.inspectionVisitUpdateRequest.instructorNote = action.payload
        },
        setIVUpdateIsRevoked: (state, action) => {
            state.inspectionVisitUpdateRequest.isRevoked = action.payload
        },
        setIVUpdateRevokedProtocolId: (state, action) => {
            state.inspectionVisitUpdateRequest.revokedProtocolId = action.payload
        },
        setIVUpdateIsNotPerformed: (state, action) => {
            state.inspectionVisitUpdateRequest.isNotPerformed = action.payload
        },
        setIVUpdateExclusionProtocolId: (state, action) => {
            state.inspectionVisitUpdateRequest.exclusionProtocolId = action.payload
        },
        setIVUpdateExclusionDate: (state, action) => {
            state.inspectionVisitUpdateRequest.exclusionDate = action.payload
        },
        setIVUpdateIsReportReturned: (state, action) => {
            state.inspectionVisitUpdateRequest.isReportReturned = action.payload
        },
        setIVUpdateRestitutionProtocolId: (state, action) => {
            state.inspectionVisitUpdateRequest.restitutionProtocolId = action.payload
        },
        setIVUpdateReturnedReportProtocolId: (state, action) => {
            state.inspectionVisitUpdateRequest.returnedReportProtocolId = action.payload
        },
        setIVUpdateNote: (state, action) => {
            state.inspectionVisitUpdateRequest.note = action.payload
        },
        setIVUpdatePlannedSampling: (state, action) => {
            state.inspectionVisitUpdateRequest.plannedSampling = action.payload
        },
        setIVUpdateAuditedActivities: (state, action) => {
            state.inspectionVisitUpdateRequest.auditedActivities = action.payload
        },
        setIVUpdateActivity: (state, action) => {
            state.inspectionVisitUpdateRequest.activity = action.payload
        },
        setIVUpdateActivityTypes: (state, action) => {
            state.inspectionVisitUpdateRequest.activityTypes = action.payload
        },
        setIVUpdateInLocoControl: (state, action) => {
            state.inspectionVisitUpdateRequest.inLocoControl = action.payload
        },
        setIVUpdateExpirationDateExtended: (state, action) => {
            state.inspectionVisitUpdateRequest.expirationDateExtended = action.payload
        },
        setIVUpdateGgNotificationId: (state, action) => {
            state.inspectionVisitUpdateRequest.ggNotificationId = action.payload
        },
        setIVUpdateSqnpiNotificationId: (state, action) => {
            state.inspectionVisitUpdateRequest.sqnpiNotificationId = action.payload
        },
        setIVUpdateMaterialType: (state, action) => {
            state.inspectionVisitUpdateRequest.materialType = action.payload
        },
        setIVUpdateFirstEnvelope: (state, action) => {
            state.inspectionVisitUpdateRequest.firstEnvelope = action.payload
        },
        setIVUpdateSecondEnvelope: (state, action) => {
            state.inspectionVisitUpdateRequest.secondEnvelope = action.payload
        },
        setIVUpdateThirdEnvelope: (state, action) => {
            state.inspectionVisitUpdateRequest.thirdEnvelope = action.payload
        },
        setIVUpdateFourthEnvelope: (state, action) => {
            state.inspectionVisitUpdateRequest.fourthEnvelope = action.payload
        },
        setIVUpdateAddOnIds: (state, action) => {
            state.inspectionVisitUpdateRequest.addOnIds = action.payload
        },
        setIVUpdateGGLotNumber: (state, action) => {
            state.inspectionVisitUpdateRequest.ggLotNumber = action.payload
        },
        setIVUpdateGGControlDuration: (state, action) => {
            state.inspectionVisitUpdateRequest.ggControlDuration = action.payload
        },
        setIVUpdateGGProducts: (state, action) => {
            state.inspectionVisitUpdateRequest.ggProducts = action.payload
        },
        setIVUpdateGGTeamLeader: (state, action) => {
            state.inspectionVisitUpdateRequest.ggTeamLeader = action.payload
        },
        setIVUpdateGGInspector: (state, action) => {
            state.inspectionVisitUpdateRequest.ggInspector = action.payload
        },
        setIVUpdateGGObserver: (state, action) => {
            state.inspectionVisitUpdateRequest.ggObserver = action.payload
        },
        setIVUpdateGGExpert: (state, action) => {
            state.inspectionVisitUpdateRequest.ggExpert = action.payload
        },
        setIVUpdateSqnpiControlActivities: (state, action) => {
            state.inspectionVisitUpdateRequest.sqnpiControlActivities = action.payload
        },
        setIVUpdateInSupportSubject: (state, action) => {
            state.inspectionVisitUpdateRequest.inSupportSubject = action.payload
        },
        setIVUpdateInSupportSubjectEntity: (state, action) => {
            state.inspectionVisitUpdateRequest.inSupportSubjectEntity = action.payload
        },
        setIVUpdateInAccompanyingSubject: (state, action) => {
            state.inspectionVisitUpdateRequest.inAccompanyingSubject = action.payload
        },
        setIVUpdateInAccompanyingSubjectEntity: (state, action) => {
            state.inspectionVisitUpdateRequest.inAccompanyingSubjectEntity = action.payload
        },
        setIVUpdateExpertSubject: (state, action) => {
            state.inspectionVisitUpdateRequest.expertSubject = action.payload
        },
        setIVUpdateTraineeTechniciant: (state, action) => {
            state.inspectionVisitUpdateRequest.traineeTechnician = action.payload
        },
        setIVUpdateAdministrativeVerification: (state, action) => {
            state.inspectionVisitUpdateRequest.administrativeVerification = action.payload
        },
        resetInspectionVisitUpdateRequest: (state) => {
            state.inspectionVisitUpdateRequest = {
                startingEndingVisits: [],
                activityTypes: [],
                auditedActivities: [],
                addOnIds: [],
                sqnpiControlActivities: [],
                daysNumber: 0
            }
        },

        // FILTERS
        setInspectionVisitFiltersItemsPerPage: (state, action) => {
            state.inspectionVisitFilters.itemsPerPage = action.payload
        },
        setInspectionVisitFiltersPage: (state, action) => {
            state.inspectionVisitFilters.page = action.payload
        },
        setInspectionVisitFiltersOrder: (state, action) => {
            state.inspectionVisitFilters.order = action.payload
        },
        setInspectionVisitFiltersSort: (state, action) => {
            state.inspectionVisitFilters.sort = action.payload
        },
        setInspectionVisitFiltersYear: (state, action) => {
            state.inspectionVisitFilters.year = action.payload
        },
        setInspectionVisitFiltersAssignmentId: (state, action) => {
            state.inspectionVisitFilters.assignmentId = action.payload
        },
        setInspectionVisitFiltersCustomerId: (state, action) => {
            state.inspectionVisitFilters.customerId = action.payload
        },
        resetInspectionVisitFilters: (state, action) => {
            state.inspectionVisitFilters = {
                itemsPerPage: 0,
                page: 0
            }
        },

        // STATUS
        resetInspectionVisitCreationStatus: (state) => {
            state.inspectionVisitCreationStatus = 'idle'
        },
        resetInspectionVisitUpdateStatus: (state) => {
            state.inspectionVisitUpdateStatus = 'idle'
        },
        resetInspectionVisitDeleteStatus: (state) => {
            state.inspectionVisitDeleteStatus = 'idle'
        },
        resetGetAllInspectionVisitStatus: (state) => {
            state.getAllInspectionVisitStatus = 'idle'
        },
        resetGetInspectionVisitByIdStatus: (state) => {
            state.getInspectionVisitByIdStatus = 'idle'
        },
        resetInspectionVisitUploadFileStatus: (state) => {
            state.inspectionVisitUploadFileStatus = 'idle'
        },

        // RESPONSE
        resetInspectionVisitUploadFileResponse: (state) => {
            state.inspectionVisitUploadFileResponse = 'idle'
        },

        /* RESULT CONTROL ACTIVITY */
        setRCACreationInspectionVisitId: (state, action) => {
            state.rcaCreationRequest.inspectionVisitId = action.payload
        },
        setRCACreationVegetalActivityResult: (state, action) => {
            state.rcaCreationRequest.vegetalActivityResult = action.payload
        },
        setRCACreationProcessingActivityResult: (state, action) => {
            state.rcaCreationRequest.processingActivityResult = action.payload
        },
        setRCACreationLivestockActivityResult: (state, action) => {
            state.rcaCreationRequest.livestockActivityResult = action.payload
        },
        setRCACreationImportationActivityResult: (state, action) => {
            state.rcaCreationRequest.importationActivityResult = action.payload
        },
        setRCACreationBlockedEvaluation: (state, action) => {
            state.rcaCreationRequest.blockedEvaluation = action.payload
        },
        setRCACreationEvaluationDate: (state, action) => {
            state.rcaCreationRequest.evaluationDate = action.payload
        },
        setRCACreationEvaluationNote: (state, action) => {
            state.rcaCreationRequest.evaluationNote = action.payload
        },
        setRCACreationCheckProcessingActivity: (state, action) => {
            state.rcaCreationRequest.checkProcessingActivity = action.payload
        },
        setRCACreationNote: (state, action) => {
            state.rcaCreationRequest.note = action.payload
        },
        setRCACreationServiceTypecode: (state, action) => {
            state.rcaCreationRequest.serviceTypecode = action.payload
        },
        setRCACreationUnitEmployed: (state, action) => {
            state.rcaCreationRequest.unitEmployed = action.payload
        },
        setRCACreationCustomerBillingWorksheetId: (state, action) => {
            state.rcaCreationRequest.customerBillingWorksheetId = action.payload
        },
        setRCACreationOfficerId: (state, action) => {
            state.rcaCreationRequest.officerId = action.payload
        },
        resetRCACreationRequest: (state) => {
            state.rcaCreationRequest = {}
        },

        setRCAIdToUpdate: (state, action) => {
            state.rcaIdToUpdate = action.payload
        },
        setRCAIVId: (state, action) => {
            state.rcaIVId = action.payload
        },
        setRCAIVCustomerId: (state, action) => {
            state.rcaIVCustomerId = action.payload
        },
        setRCAUPdateRequest: (state, action) => {
            state.rcaUpdateRequest = action.payload
        },
        setRCAUpdateVegetalActivityResult: (state, action) => {
            state.rcaUpdateRequest.vegetalActivityResult = action.payload
        },
        setRCAUpdateProcessingActivityResult: (state, action) => {
            state.rcaUpdateRequest.processingActivityResult = action.payload
        },
        setRCAUpdateLivestockActivityResult: (state, action) => {
            state.rcaUpdateRequest.livestockActivityResult = action.payload
        },
        setRCAUpdateImportationActivityResult: (state, action) => {
            state.rcaUpdateRequest.importationActivityResult = action.payload
        },
        setRCAUpdateBlockedEvaluation: (state, action) => {
            state.rcaUpdateRequest.blockedEvaluation = action.payload
        },
        setRCAUpdateEvaluationDate: (state, action) => {
            state.rcaUpdateRequest.evaluationDate = action.payload
        },
        setRCAUpdateEvaluationNote: (state, action) => {
            state.rcaUpdateRequest.evaluationNote = action.payload
        },
        setRCAUpdateCheckProcessingActivity: (state, action) => {
            state.rcaUpdateRequest.checkProcessingActivity = action.payload
        },
        setRCAUpdateNote: (state, action) => {
            state.rcaUpdateRequest.note = action.payload
        },
        setRCAUpdateServiceTypecode: (state, action) => {
            state.rcaUpdateRequest.serviceTypecode = action.payload
        },
        setRCAUpdateUnitEmployed: (state, action) => {
            state.rcaUpdateRequest.unitEmployed = action.payload
        },
        setRCAUpdateCustomerBillingWorksheetId: (state, action) => {
            state.rcaUpdateRequest.customerBillingWorksheetId = action.payload
        },
        setRCAUpdateOfficerId: (state, action) => {
            state.rcaUpdateRequest.officerId = action.payload
        },
        resetRCAUpdateRequest: (state) => {
            state.rcaUpdateRequest = {}
        },

        // STATUS
        resetRCACreationStatus: (state) => {
            state.rcaCreationStatus = 'idle'
        },
        resetRCAUpdateSatus: (state) => {
            state.rcaUpdateStatus = 'idle'
        },
        resetRCADeleteStatus: (state) => {
            state.rcaDeleteStatus = 'idle'
        },
        resetGetAllRCAStatus: (state) => {
            state.getAllRcaStatus = 'idle'
        },
        resetGetRCAByIdStatus: (state) => {
            state.getRcaByIdStatus = 'idle'
        },
        resetUpdateFileSignedAssign: (state) => {
            state.fileAssignSignedUploadStatus = 'idle'
        },
        resetFileAssSigned: (state) => {
            state.fileAssSignedErrors = {
                file: false
            }
        },
    },
    extraReducers(builder) {
        builder

            /* ASSIGNMENT */
            // CREATION
            .addCase(AssignmentCreation.pending, (state) => {
                state.assignmentCreationStatus = 'loading'
            })
            .addCase(AssignmentCreation.fulfilled, (state, action) => {
                state.assignmentCreationStatus = 'successfully'
                state.assignmentCreationResponse = action.payload
            })
            .addCase(AssignmentCreation.rejected, (state, action) => {
                state.assignmentCreationStatus = 'failed'
            })

            // UPDATE
            .addCase(AssignmentUpdate.pending, (state) => {
                state.assignmentUpdateStatus = 'loading'
            })
            .addCase(AssignmentUpdate.fulfilled, (state) => {
                state.assignmentUpdateStatus = 'successfully'
            })
            .addCase(AssignmentUpdate.rejected, (state, action) => {
                state.assignmentUpdateStatus = 'failed'
            })

            .addCase(UploadFileAssSigned.pending, (state) => {
                state.fileAssignSignedUploadStatus = 'loading'
            })
            .addCase(UploadFileAssSigned.fulfilled, (state) => {
                state.fileAssignSignedUploadStatus = 'successfully'
            })
            .addCase(UploadFileAssSigned.rejected, (state, action) => {
                state.fileAssignSignedUploadStatus = 'failed'
            })

            // DELETE
            .addCase(AssignmentDelete.pending, (state) => {
                state.assignmentDeleteStatus = 'loading'
            })
            .addCase(AssignmentDelete.fulfilled, (state) => {
                state.assignmentDeleteStatus = 'successfully'
            })
            .addCase(AssignmentDelete.rejected, (state, action) => {
                state.assignmentDeleteStatus = 'failed'
            })

            // GET ALL
            .addCase(GetAllAssignment.pending, (state) => {
                state.getAllAssignmentStatus = 'loading'
            })
            .addCase(GetAllAssignment.fulfilled, (state, action) => {
                state.getAllAssignmentStatus = 'successfully'
                state.getAllAssignmentResponse = action.payload
            })
            .addCase(GetAllAssignment.rejected, (state, action) => {
                state.getAllAssignmentStatus = 'failed'
            })

            // GET BY ID
            .addCase(GetAssignmentById.pending, (state) => {
                state.getAssignmentByIdStatus = 'loading'
            })
            .addCase(GetAssignmentById.fulfilled, (state, action) => {
                state.getAssignmentByIdStatus = 'successfully'
                state.getAssignmentByIdResponse = action.payload
            })
            .addCase(GetAssignmentById.rejected, (state, action) => {
                state.getAssignmentByIdStatus = 'failed'
            })

            // VALIDATION
            .addCase(AssignmentValidation.pending, (state) => {
                state.assignmentValidationStatus = 'loading'
            })
            .addCase(AssignmentValidation.fulfilled, (state) => {
                state.assignmentValidationStatus = 'successfully'
            })
            .addCase(AssignmentValidation.rejected, (state, action) => {
                state.assignmentValidationStatus = 'failed'
            })

            .addCase(fileAssignSignedValidation.pending, (state) => {
                state.fileAssignSignedValidationStatus = 'loading'
            })
            .addCase(fileAssignSignedValidation.fulfilled, (state) => {
                state.fileAssignSignedValidationStatus = 'successfully'
            })
            .addCase(fileAssignSignedValidation.rejected, (state) => {
                state.fileAssignSignedValidationStatus = 'failed'
            })

            /* INSPECTION VISIT */
            // CREATION
            .addCase(InspectionVisitCreation.pending, (state) => {
                state.inspectionVisitCreationStatus = 'loading'
            })
            .addCase(InspectionVisitCreation.fulfilled, (state) => {
                state.inspectionVisitCreationStatus = 'successfully'
            })
            .addCase(InspectionVisitCreation.rejected, (state, action) => {
                state.inspectionVisitCreationStatus = 'failed'
            })

            // UPDATE
            .addCase(InspectionVisitUpdate.pending, (state) => {
                state.inspectionVisitUpdateStatus = 'loading'
            })
            .addCase(InspectionVisitUpdate.fulfilled, (state) => {
                state.inspectionVisitUpdateStatus = 'successfully'
            })
            .addCase(InspectionVisitUpdate.rejected, (state, action) => {
                state.inspectionVisitUpdateStatus = 'failed'
            })

            // DELETE
            .addCase(InspectionVisitDelete.pending, (state) => {
                state.inspectionVisitDeleteStatus = 'loading'
            })
            .addCase(InspectionVisitDelete.fulfilled, (state) => {
                state.inspectionVisitDeleteStatus = 'successfully'
            })
            .addCase(InspectionVisitDelete.rejected, (state, action) => {
                state.inspectionVisitDeleteStatus = 'failed'
            })

            // GET ALL
            .addCase(GetAllInspectionVisit.pending, (state) => {
                state.getAllInspectionVisitStatus = 'loading'
            })
            .addCase(GetAllInspectionVisit.fulfilled, (state, action) => {
                state.getAllInspectionVisitStatus = 'successfully'
                state.getAllInspectionVisitResponse = action.payload
            })
            .addCase(GetAllInspectionVisit.rejected, (state, action) => {
                state.getAllInspectionVisitStatus = 'failed'
            })

            // GET BY ID
            .addCase(GetInspectionVisitById.pending, (state) => {
                state.getInspectionVisitByIdStatus = 'loading'
            })
            .addCase(GetInspectionVisitById.fulfilled, (state, action) => {
                state.getInspectionVisitByIdStatus = 'successfully'
                state.getInspectionVisitByIdResponse = action.payload
            })
            .addCase(GetInspectionVisitById.rejected, (state, action) => {
                state.getInspectionVisitByIdStatus = 'failed'
            })

            // UPLOAD FILE
            .addCase(InspectionVisitUploadFile.pending, (state) => {
                state.inspectionVisitUploadFileStatus = 'loading'
            })
            .addCase(InspectionVisitUploadFile.fulfilled, (state, action) => {
                state.inspectionVisitUploadFileStatus = 'successfully'
                state.inspectionVisitUploadFileResponse = action.payload
            })
            .addCase(InspectionVisitUploadFile.rejected, (state, action) => {
                state.inspectionVisitUploadFileStatus = 'failed'
            })

            /* RESULT CONTROL ACTIVITY */
            // CREATION
            .addCase(ResultControlActivityCreation.pending, (state) => {
                state.rcaCreationStatus = 'loading'
            })
            .addCase(ResultControlActivityCreation.fulfilled, (state) => {
                state.rcaCreationStatus = 'successfully'
            })
            .addCase(ResultControlActivityCreation.rejected, (state, action) => {
                state.rcaCreationStatus = 'failed'
            })

            // UPDATE
            .addCase(ResultControlActivityUpdate.pending, (state) => {
                state.rcaUpdateStatus = 'loading'
            })
            .addCase(ResultControlActivityUpdate.fulfilled, (state) => {
                state.rcaUpdateStatus = 'successfully'
            })
            .addCase(ResultControlActivityUpdate.rejected, (state, action) => {
                state.rcaUpdateStatus = 'failed'
            })

            // DELETE
            .addCase(ResultControlActivityDelete.pending, (state) => {
                state.rcaDeleteStatus = 'loading'
            })
            .addCase(ResultControlActivityDelete.fulfilled, (state) => {
                state.rcaDeleteStatus = 'successfully'
            })
            .addCase(ResultControlActivityDelete.rejected, (state, action) => {
                state.rcaDeleteStatus = 'failed'
            })

            // GET ALL
            .addCase(GetAllResultControlActivity.pending, (state) => {
                state.getAllRcaStatus = 'loading'
            })
            .addCase(GetAllResultControlActivity.fulfilled, (state, action) => {
                state.getAllRcaStatus = 'successfully'
                state.getAllRcaResponse = action.payload
            })
            .addCase(GetAllResultControlActivity.rejected, (state, action) => {
                state.getAllRcaStatus = 'failed'
            })

            // GET BY ID
            .addCase(GetResultControlActivityById.pending, (state) => {
                state.getRcaByIdStatus = 'loading'
            })
            .addCase(GetResultControlActivityById.fulfilled, (state, action) => {
                state.getRcaByIdStatus = 'successfully'
                state.getRcaByIdResponse = action.payload
            })
            .addCase(GetResultControlActivityById.rejected, (state, action) => {
                state.getRcaByIdStatus = 'failed'
            })

            // GET BY ID
            .addCase(InspectionVisitTurnOver.pending, (state) => {
                state.inspectionVisitTurnOverStatus = 'loading'
            })
            .addCase(InspectionVisitTurnOver.fulfilled, (state, action) => {
                state.inspectionVisitTurnOverStatus = 'successfully'
                state.turnOverResponse = action.payload
            })
            .addCase(InspectionVisitTurnOver.rejected, (state, action) => {
                state.inspectionVisitTurnOverStatus = 'failed'
            })

    },
})

export const {
    /* ASSIGNMENT */
    setOnEdit,
    setAssignmentProtocolTextPopup,
    setAssignmentSelected,
    // REQUEST
    setAssCreationAssignmentDate,
    setAssignmentSignedFileId,
    setAssCreationAssignmentPlace,
    setAssCreationDepartureProtocolId,
    setAssCreationNorm,
    setAssCreationPersonalManagementId,
    setAssCreationPmContractId,
    setAssCreationReference,
    setAssCreationYear,
    setAssignmentUpdateRequest,
    setAssignmentPMId,
    setAssignmentUpdateIsAccepted,
    setAssUpdatePMContractId,
    setAssUpdateReference,
    setAssUpdateRevocationProtocolId,
    setAssUpdateSignedAssignmentLetterId,
    setAssUpdateState,
    setAssUpdateAssignmentUcoVerDate,
    setAssUpdateAssignmentUcoVerUserId,
    setAssignmentIdToUpdate,
    setAssUpdateDepartureProtocolId,
    setAssUpdateAssignmentPlace,
    setFileAssignSigned,
    setAssContractType,
    setAssHeadQuarterId,
    resetAssignmentCreationRequest,
    resetAssignmentUpdateRequest,

    // ERRORS
    setValidateAssignmentDate,
    setValidateAssignmentPlace,
    setValidateDepartureProtocolId,
    setValidateNorm,
    setValidatePersonalManagementId,
    setValidateYear,
    setValidateHeadQuarterId,
    resetAssignmentErrors,
    setValidateFileAssSisgned,

    // FILTERS
    setAssignmentFilterInspectorCode,
    setAssignmentFilterItemsPerPage,
    setAssignmentFilterOrder,
    setAssignmentFilterPage,
    setAssignmentFilterSort,
    setAssignmentFilterYear,
    resetAssignmentFilters,

    // STATUS
    resetAssignmentValidationStatus,
    resetAssigmentCreationStatus,
    resetAssigmentDeleteStatus,
    resetAssigmentUpdateStatus,
    resetGetAllAssignmentStatus,
    resetGetAssignmentByIdStatus,
    resetTurnOverStatus,

    // RESPONSE
    resetAssignmentCreationResponse,
    resetGetAssignmentByIdResponse,

    /* INSPECTION VISIT */
    setIVToDelete,
    setIVCreationAnnualControlPlaneDetailId,
    setIVCreationAssignmentId,
    setIVCreationBioNotificationId,
    setIVCreationControlDetail,
    setIVCreationCustomerId,
    setIVCreationOfficerId,
    setIVCreationExpertSubject,
    setIVCreationExpirationDate,
    setIVCreationInAccompanyingSubject,
    setIVCreationAddOnIds,
    setIVCreationFirstEnvelope,
    setIVCreationFourthEnvelope,
    setIVCreationGGControlDuration,
    setIVCreationGGExpert,
    setIVCreationGGInspector,
    setIVCreationGGLotNumber,
    setIVCreationGGObserver,
    setIVCreationGGProducts,
    setIVCreationGGTeamLeader,
    setIVCreationMaterialType,
    setIVCreationSecondEnvelope,
    setIVCreationSqnpiControlActivities,
    setIVCreationThirdEnvelope,
    setIVCreationInAccompanyingSubjectEntity,
    setIVCreationInSupportSubject,
    setIVCreationInSupportSubjectEntity,
    setIVCreationLicenseeOperator,
    setIVCreationMode,
    setIVCreationSpecification,
    setIVCreationTraineeTechnician,
    setIVCreationType,
    setInspectionVisitUpdateRequest,
    setIVUpdateBioNotificationId,
    setIVUpdateControlDetail,
    setIVUpdateDaysNumber,
    setIVUpdateDocumentalControl,
    setIVUpdateEffectiveVisitDate,
    setIVUpdateExclusionProtocolId,
    setIVUpdateExpirationDate,
    setIVUpdateInMoreDays,
    setIVUpdateInstructorNote,
    setIVUpdateIsNotPerformed,
    setIVUpdateIsReportReturned,
    setIVUpdateIsRevoked,
    setIVUpdateLicenseeOperator,
    setIVUpdateMode,
    setIVUpdateNonComplianceType,
    setIVUpdateNote,
    setIVUpdatePlannedVisitDate,
    setIVUpdateRemoteVisit,
    setIVUpdateAddOnIds,
    setIVUpdateFirstEnvelope,
    setIVUpdateFourthEnvelope,
    setIVUpdateGGControlDuration,
    setIVUpdateGGExpert,
    setIVUpdateGGInspector,
    setIVUpdateGGLotNumber,
    setIVUpdateGGObserver,
    setIVUpdateGGProducts,
    setIVUpdateGGTeamLeader,
    setIVUpdateGgNotificationId,
    setIVUpdateMaterialType,
    setIVUpdateSecondEnvelope,
    setIVUpdateSqnpiControlActivities,
    setIVUpdateSqnpiNotificationId,
    setIVUpdateThirdEnvelope,
    setIVUpdateExpertSubject,
    setIVUpdateInAccompanyingSubject,
    setIVUpdateInAccompanyingSubjectEntity,
    setIVUpdateInSupportSubject,
    setIVUpdateInSupportSubjectEntity,
    setIVUpdateTraineeTechniciant,
    setIVUpdateExclusionDate,
    setIVUpdateAdministrativeVerification,
    setIVUpdateRemoteVisitProtocolId,
    setIVUpdateReportProtocolId,
    setIVUpdateRestitutionProtocolId,
    setIVUpdateResult,
    setIVUpdateReturnedReportProtocolId,
    setIVUpdateRevokedProtocolId,
    setIVUpdateSpecification,
    setIVUpdateStartingEndingVisits,
    setIVFileToUpload,
    setIVUploadedFileId,
    setIVUpdateStartingEndingVisitsByIndex,
    addIVUpdateDefaultStartingEndingVisits,
    addIVUpdateStartingEndingVisits,
    setIVUpdateType,
    setIVUpdateVisitDate,
    setInspectionVisitIdToUpdate,
    setIVCreationActivity,
    setIVCreationActivityTypes,
    setIVCreationAuditedActivities,
    setIVCreationPlannedSampling,
    setIVUpdateActivity,
    setIVUpdateActivityTypes,
    setIVUpdateAuditedActivities,
    setIVUpdatePlannedSampling,
    setIVCreationDocumentalControl,
    setIVCreationExpirationDateExtended,
    setIVCreationInLocoControl,
    setIVCreationRemoteVisit,
    setIVUpdateExpirationDateExtended,
    setIVUpdateInLocoControl,
    resetInspectionVisitRequest,
    resetInspectionVisitUpdateRequest,

    // FILTERS
    setInspectionVisitFiltersAssignmentId,
    setInspectionVisitFiltersCustomerId,
    setInspectionVisitFiltersItemsPerPage,
    setInspectionVisitFiltersOrder,
    setInspectionVisitFiltersPage,
    setInspectionVisitFiltersSort,
    setInspectionVisitFiltersYear,
    resetInspectionVisitFilters,

    // STATUS
    resetGetAllInspectionVisitStatus,
    resetGetInspectionVisitByIdStatus,
    resetInspectionVisitCreationStatus,
    resetInspectionVisitDeleteStatus,
    resetInspectionVisitUpdateStatus,
    resetInspectionVisitUploadFileStatus,

    // RESPONSE
    resetInspectionVisitUploadFileResponse,

    /* RESULT CONTROL ACTIVITY */
    // REQUEST
    setRCACreationBlockedEvaluation,
    setRCACreationCheckProcessingActivity,
    setRCACreationCustomerBillingWorksheetId,
    setRCACreationEvaluationDate,
    setRCACreationEvaluationNote,
    setRCACreationImportationActivityResult,
    setRCACreationInspectionVisitId,
    setRCACreationLivestockActivityResult,
    setRCACreationNote,
    setRCACreationOfficerId,
    setRCACreationProcessingActivityResult,
    setRCACreationServiceTypecode,
    setRCACreationUnitEmployed,
    setRCACreationVegetalActivityResult,
    setRCAIdToUpdate,
    setRCAUpdateBlockedEvaluation,
    setRCAUpdateCheckProcessingActivity,
    setRCAUpdateCustomerBillingWorksheetId,
    setRCAUpdateEvaluationDate,
    setRCAUpdateEvaluationNote,
    setRCAUpdateImportationActivityResult,
    setRCAUpdateLivestockActivityResult,
    setRCAUpdateNote,
    setRCAUpdateOfficerId,
    setRCAIVId,
    setRCAIVCustomerId,
    setRCAUpdateProcessingActivityResult,
    setRCAUpdateServiceTypecode,
    setRCAUpdateUnitEmployed,
    setRCAUpdateVegetalActivityResult,
    setRCAUPdateRequest,
    resetRCACreationRequest,
    resetRCAUpdateRequest,

    // STATUS
    resetGetAllRCAStatus,
    resetGetRCAByIdStatus,
    resetRCACreationStatus,
    resetRCADeleteStatus,
    resetRCAUpdateSatus,
    resetFileAssSignedValidationStatus,
    resetFileAssSigned,
    resetUpdateFileSignedAssign
} = AssignmentSlice.actions

export default AssignmentSlice.reducer

